/**
 * Product Card Cart Toggle
 *
 * Manages the visibility of add-to-cart button vs quantity selector on product cards
 * based on whether the product is in the cart. Uses theme's default add to cart
 * and cart change APIs without modifying existing JS files.
 */
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

const SELECTOR = {
  toggle: "[data-product-card-cart-toggle]",
  quantityWrapper: "[data-product-card-quantity-wrapper]",
  addToCartWrapper: "[data-product-card-add-to-cart-wrapper]",
  quantitySelector: "quantity-selector-component",
};

let isInitialized = false;
let syncRafId = null;
let layoutRafId = null;
let toggleResizeObserver = null;
let toggleMutationObserver = null;
const PRODUCT_RECOMMENDATIONS_LOADED_EVENT = "product-recommendations:loaded";

/**
 * Gets cart section IDs for cart change/update requests
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document
    .querySelectorAll("cart-items-component[data-section-id]")
    .forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
  return Array.from(ids);
}

/**
 * Fetches the current cart
 * @returns {Promise<{items: Array<{variant_id: number, quantity: number}>}>}
 */
async function fetchCart() {
  const response = await fetch("/cart.js");
  return response.json();
}

/**
 * Finds the cart line index (1-based) for a variant ID
 * @param {Object} cart
 * @param {string} variantId
 * @returns {number|null}
 */
function getLineForVariant(cart, variantId) {
  if (!cart?.items?.length) return null;
  const index = cart.items.findIndex(
    (item) => item.variant_id.toString() === variantId.toString(),
  );
  return index >= 0 ? index + 1 : null;
}

/**
 * Gets cart quantity for a variant
 * @param {Object} cart
 * @param {string} variantId
 * @returns {number}
 */
function getCartQuantityForVariant(cart, variantId) {
  const item = cart?.items?.find(
    (i) => i.variant_id.toString() === variantId.toString(),
  );
  return item ? item.quantity : 0;
}

/**
 * Updates cart quantity via theme's cart change API
 * @param {number} line
 * @param {number} quantity
 * @returns {Promise<Object>}
 */
async function updateCartQuantity(line, quantity) {
  const sectionIds = getCartSectionIds();
  const body = JSON.stringify({
    line,
    quantity,
    sections: sectionIds.join(","),
    sections_url: window.location.pathname,
  });

  const response = await fetch(
    Theme.routes.cart_change_url,
    fetchConfig("json", { body }),
  );
  const text = await response.text();
  return JSON.parse(text);
}

/**
 * Switches a product card to cart mode (show quantity, hide add-to-cart)
 * @param {HTMLElement} toggle
 * @param {number} cartQuantity
 */
function switchToCartMode(toggle, cartQuantity) {
  const quantityWrapper = toggle.querySelector(SELECTOR.quantityWrapper);
  const addToCartWrapper = toggle.querySelector(SELECTOR.addToCartWrapper);
  const quantitySelector = toggle.querySelector(SELECTOR.quantitySelector);
  const input = quantitySelector?.querySelector('input[name="quantity"]');

  if (quantityWrapper) quantityWrapper.classList.remove("hidden");
  if (addToCartWrapper) addToCartWrapper.classList.add("hidden");

  if (input && quantitySelector) {
    input.value = String(cartQuantity);
    input.setAttribute("data-cart-quantity", "0");
    input.min = "0";
    if (quantitySelector.updateCartQuantity) {
      quantitySelector.updateCartQuantity();
    }
    if (quantitySelector.updateButtonStates) {
      quantitySelector.updateButtonStates();
    }
  }

  toggle.dataset.cartMode = "true";
  updateToggleAddToCartWidth(toggle);
}

/**
 * Switches a product card to add mode (show add-to-cart, hide quantity)
 * @param {HTMLElement} toggle
 */
function switchToAddMode(toggle) {
  const quantityWrapper = toggle.querySelector(SELECTOR.quantityWrapper);
  const addToCartWrapper = toggle.querySelector(SELECTOR.addToCartWrapper);
  const quantitySelector = toggle.querySelector(SELECTOR.quantitySelector);
  const variantIdInput = toggle.querySelector('input[name="id"]');

  if (quantityWrapper) quantityWrapper.classList.add("hidden");
  if (addToCartWrapper) addToCartWrapper.classList.remove("hidden");

  if (quantitySelector && variantIdInput) {
    const quantityInput = quantitySelector.querySelector(
      'input[name="quantity"]',
    );
    const minQty = quantityInput?.getAttribute("data-min") || "1";
    if (quantityInput) {
      quantityInput.value = minQty;
      quantityInput.setAttribute("data-cart-quantity", "0");
      quantityInput.min = minQty;
      if (quantitySelector.updateCartQuantity) {
        quantitySelector.updateCartQuantity();
      }
      if (quantitySelector.updateButtonStates) {
        quantitySelector.updateButtonStates();
      }
    }
  }

  delete toggle.dataset.cartMode;
  updateToggleAddToCartWidth(toggle);
}

/**
 * Keeps add-to-cart width constrained when inline with price, and full-width when wrapped.
 * @param {HTMLElement} toggle
 */
function updateToggleAddToCartWidth(toggle) {
  const addToCartWrapper = toggle.querySelector(SELECTOR.addToCartWrapper);
  const quantityWrapper = toggle.querySelector(SELECTOR.quantityWrapper);
  const quantitySelector = quantityWrapper?.querySelector(
    "quantity-selector-component, cart-quantity-selector-component, .quantity-selector",
  );
  const quantityInput = quantitySelector?.querySelector('input[type="number"]');
  const quantityButtons = quantitySelector?.querySelectorAll('button');
  const form = toggle.querySelector(".product-card-cart-toggle__form");
  if (!(form instanceof HTMLElement)) return;

  const priceNode = toggle.querySelector('[ref="priceContainer"]');
  if (!(priceNode instanceof HTMLElement)) return;

  const priceRect = priceNode.getBoundingClientRect();
  const formRect = form.getBoundingClientRect();
  const wrappedToNextLine = formRect.top >= priceRect.bottom - 1;

  if (addToCartWrapper instanceof HTMLElement) {
    addToCartWrapper.style.maxWidth = wrappedToNextLine ? "" : "160px";
    addToCartWrapper.style.marginLeft = wrappedToNextLine ? "" : "auto";
  }

  if (quantityWrapper instanceof HTMLElement) {
    quantityWrapper.style.width = "100%";
    quantityWrapper.style.maxWidth = wrappedToNextLine ? "" : "160px";
    quantityWrapper.style.marginLeft = wrappedToNextLine ? "" : "auto";
  }

  if (quantitySelector instanceof HTMLElement) {
    quantitySelector.style.width = "100%";
    quantitySelector.style.maxWidth = "100%";
    quantitySelector.style.display = "flex";
    quantitySelector.style.alignItems = "stretch";
    quantitySelector.style.justifyContent = "stretch";
  }

  if (quantityInput instanceof HTMLInputElement) {
    quantityInput.style.width = "0";
    quantityInput.style.maxWidth = "none";
    quantityInput.style.flex = "1 1 0";
    quantityInput.style.minWidth = "0";
  }

  if (quantityButtons) {
    quantityButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.style.width = "0";
      button.style.maxWidth = "none";
      button.style.flex = "1 1 0";
      button.style.minWidth = "0";
    });
  }
}

/**
 * Reconnects resize observers so width calculations re-run when card layout changes.
 */
function refreshToggleLayoutObserver() {
  if (typeof ResizeObserver === "undefined") return;

  if (toggleResizeObserver) {
    toggleResizeObserver.disconnect();
  }

  toggleResizeObserver = new ResizeObserver(() => {
    queueUpdateToggleAddToCartWidths();
  });

  document.querySelectorAll(SELECTOR.toggle).forEach((toggle) => {
    if (toggle instanceof HTMLElement) {
      toggleResizeObserver.observe(toggle);
    }
  });
}

/**
 * Returns true when a newly added node contains product-card cart toggles.
 * @param {Node} node
 * @returns {boolean}
 */
function nodeContainsToggle(node) {
  if (!(node instanceof HTMLElement)) return false;
  return node.matches(SELECTOR.toggle) || Boolean(node.querySelector(SELECTOR.toggle));
}

/**
 * Watches DOM additions (e.g. collection "Load more") and syncs newly inserted cards.
 */
function initToggleMutationObserver() {
  if (typeof MutationObserver === "undefined") return;
  if (!(document.body instanceof HTMLElement)) return;

  if (toggleMutationObserver) {
    toggleMutationObserver.disconnect();
  }

  toggleMutationObserver = new MutationObserver((mutations) => {
    let hasNewToggle = false;

    for (const mutation of mutations) {
      if (mutation.type !== "childList" || mutation.addedNodes.length === 0) {
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (nodeContainsToggle(node)) {
          hasNewToggle = true;
          break;
        }
      }

      if (hasNewToggle) break;
    }

    if (!hasNewToggle) return;

    refreshToggleLayoutObserver();
    queueSyncProductCards();
    queueUpdateToggleAddToCartWidths();
  });

  toggleMutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * Updates add-to-cart width behavior for all product card toggles.
 */
function updateAllToggleAddToCartWidths() {
  document.querySelectorAll(SELECTOR.toggle).forEach((toggle) => {
    if (toggle instanceof HTMLElement) {
      updateToggleAddToCartWidth(toggle);
    }
  });
}

/**
 * Queues width updates for the next animation frame.
 */
function queueUpdateToggleAddToCartWidths() {
  if (layoutRafId !== null) return;
  layoutRafId = requestAnimationFrame(() => {
    layoutRafId = null;
    updateAllToggleAddToCartWidths();
  });
}

/**
 * Syncs all product card buy buttons with current cart state
 * @param {Object} [cart] - Optional cart object; fetches if not provided
 */
async function syncProductCards(cart) {
  const toggles = document.querySelectorAll(SELECTOR.toggle);
  if (!toggles.length) return;

  const cartData = cart ?? (await fetchCart());

  toggles.forEach((toggle) => {
    const variantIdInput = toggle.querySelector('input[name="id"]');
    const variantId = variantIdInput?.value;
    if (!variantId) return;

    const cartQty = getCartQuantityForVariant(cartData, variantId);

    if (cartQty > 0) {
      switchToCartMode(toggle, cartQty);
    } else {
      switchToAddMode(toggle);
    }
  });

  refreshToggleLayoutObserver();
  queueUpdateToggleAddToCartWidths();
}

/**
 * Queues a sync on the next animation frame
 */
function queueSyncProductCards() {
  if (syncRafId !== null) return;

  syncRafId = requestAnimationFrame(async () => {
    syncRafId = null;
    try {
      await syncProductCards();
    } catch (error) {
      console.error(
        "Product card cart toggle: failed to sync product cards",
        error,
      );
    }

    queueUpdateToggleAddToCartWidths();
  });
}

/**
 * Handles quantity selector update from a product card in cart mode
 * @param {QuantitySelectorUpdateEvent} event
 */
async function handleQuantityUpdate(event) {
  const target = event.target;
  if (!(target instanceof Node)) return;

  const toggle = target.closest(SELECTOR.toggle);
  if (!toggle || toggle.dataset.cartMode !== "true") return;

  const variantIdInput = toggle.querySelector('input[name="id"]');
  const variantId = variantIdInput?.value;
  if (!variantId) return;

  const { quantity: newQuantity } = event.detail;
  if (typeof newQuantity !== "number" || newQuantity < 0) return;

  const cart = await fetchCart();
  const line = getLineForVariant(cart, variantId);
  if (!line) return;
  const previousQuantity = getCartQuantityForVariant(cart, variantId);

  try {
    const response = await updateCartQuantity(line, newQuantity);

    if (response?.status && response.status >= 400) {
      const input = toggle.querySelector(
        `${SELECTOR.quantitySelector} input[name="quantity"]`,
      );
      if (input) {
        const prevQty = getCartQuantityForVariant(cart, variantId);
        input.value = String(prevQty);
      }
      return;
    }

    if (newQuantity === 0) {
      switchToAddMode(toggle);
    } else {
      const input = toggle.querySelector(
        `${SELECTOR.quantitySelector} input[name="quantity"]`,
      );
      if (input) {
        input.value = String(newQuantity);
        input.setAttribute("data-cart-quantity", "0");
        const qs = toggle.querySelector(SELECTOR.quantitySelector);
        if (qs?.updateButtonStates) qs.updateButtonStates();
      }
    }

    const itemCount = response?.item_count ?? 0;
    const sections = response?.sections ?? {};
    let changeType = "decrease";
    if (newQuantity > previousQuantity) {
      changeType = "increase";
    } else if (newQuantity === 0) {
      changeType = "remove";
    }

    const productForm = toggle.querySelector(".product-card-cart-toggle__form");
    const productId = productForm?.dataset.productId;
    const productTitle = toggle
      .closest(".resource-card, .product-grid__card, product-card")
      ?.querySelector("[ref='productTitleLink'], .resource-card__title")
      ?.textContent?.trim();

    document.dispatchEvent(
      new CartUpdateEvent(response, "product-card-cart-toggle", {
        itemCount,
        source: "product-card-cart-toggle",
        sections,
        productId,
        variantId,
        quantity: newQuantity,
        previousQuantity,
        changeType,
        productTitle,
      }),
    );
  } catch (error) {
    console.error("Product card cart toggle: failed to update quantity", error);
    const input = toggle.querySelector(
      `${SELECTOR.quantitySelector} input[name="quantity"]`,
    );
    if (input) {
      const cartData = await fetchCart();
      const prevQty = getCartQuantityForVariant(cartData, variantId);
      input.value = String(prevQty);
    }
  }
}

/**
 * Handles add to cart success - switch matching product card to quantity mode
 * @param {CartAddEvent} event
 */
async function handleCartAdd(event) {
  const { source, productId } = event.detail?.data ?? {};
  if (source !== "product-form-component" || !productId) return;
  await syncProductCards();
}

/**
 * Handles cart update from other sources (e.g. cart page) - sync product cards
 * Skips when source is product-form-component (handled by handleCartAdd) or product-card-cart-toggle
 * @param {CartUpdateEvent} event
 */
function handleCartUpdate(event) {
  const source = event.detail?.data?.source;
  if (
    source === "product-card-cart-toggle" ||
    source === "product-form-component"
  )
    return;
  const cart = event.detail?.resource;
  if (cart?.items?.length) {
    syncProductCards(cart);
  } else {
    syncProductCards();
  }
}

/**
 * Handles add to cart button click - immediately switches to quantity selector
 * @param {MouseEvent} event
 */
function handleAddToCartClick(event) {
  const addToCartWrapper = event.target.closest(SELECTOR.addToCartWrapper);
  if (!addToCartWrapper) return;

  const toggle = addToCartWrapper.closest(SELECTOR.toggle);
  if (!toggle) return;

  const form = toggle.querySelector("form");
  const quantityInput = form?.querySelector('input[name="quantity"]');
  const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

  switchToCartMode(toggle, quantity);
  queueUpdateToggleAddToCartWidths();
}

function init() {
  if (isInitialized) return;
  isInitialized = true;

  document.addEventListener("click", handleAddToCartClick);
  document.addEventListener(
    ThemeEvents.quantitySelectorUpdate,
    handleQuantityUpdate,
  );
  document.addEventListener(ThemeEvents.cartUpdate, handleCartAdd);
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
  document.addEventListener(
    PRODUCT_RECOMMENDATIONS_LOADED_EVENT,
    queueSyncProductCards,
  );
  window.addEventListener("resize", queueUpdateToggleAddToCartWidths);
  window.addEventListener("load", queueUpdateToggleAddToCartWidths);

  initToggleMutationObserver();
  refreshToggleLayoutObserver();
  queueSyncProductCards();
  queueUpdateToggleAddToCartWidths();

  // Recalculate once more after initial paint to avoid transient first-render widths.
  requestAnimationFrame(() => queueUpdateToggleAddToCartWidths());
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
